import { Theme } from '@mui/material';
import { format, startOfMonth, sub } from 'date-fns';

type ChartLineDataStyledProps = {
  labels: string[];
  data: number[];
  theme: Theme;
  tooltipLabel: string;
};

type ChartLineDataStyledWithColorIndicatorProps = {
  labels: string[];
  data: number[];
  colorIndicator: string;
  tooltipLabel: string;
};

type GetFilterPeriodDatesResponse = {
  startDateFormattedToQuery: string;
  endDateFormattedToQuery: string;
};

type GetValueColorIndicatorProps = {
  maximumIndicator: MaximumvalueIndicators;
  theme: Theme;
  value?: number;
};

export enum DatePeriodFilters {
  CUSTOM_DATE = -1,
  ONE_MONTH = 1,
  THREE_MONTHS = 3,
  SIX_MONTHS = 6,
  ONE_YEAR = 12,
}

export enum MaximumvalueIndicators {
  PERCENT = 100,
  ONE_TO_TEN = 10,
  ONE_TO_FIVE = 5,
}

export const trendFilters = [
  DatePeriodFilters.ONE_MONTH,
  DatePeriodFilters.THREE_MONTHS,
  DatePeriodFilters.SIX_MONTHS,
];

export const checkinReportsFilters = [
  DatePeriodFilters.ONE_MONTH,
  DatePeriodFilters.THREE_MONTHS,
  DatePeriodFilters.SIX_MONTHS,
  DatePeriodFilters.ONE_YEAR,
];

export const chartOptions = (): any => ({
  responsive: true,
  scales: {
    x: {
      offset: true,
    },
  },
  plugins: {
    legend: {
      display: false,
    },
    tooltip: {
      displayColors: false,
    },
  },
});

export const percentChartOptions = (): any => ({
  responsive: true,
  scales: {
    x: {
      offset: true,
    },
    y: {
      beginAtZero: true,
      ticks: {
        min: 0,
        max: 100,
        callback(value: any) {
          return `${value}%`;
        },
      },
      scaleLabel: {
        display: true,
        labelString: 'Percentage',
      },
    },
  },
  plugins: {
    legend: {
      display: false,
    },
    tooltip: {
      displayColors: false,
      callbacks: {
        label(tooltipItem: any) {
          return `${tooltipItem.dataset.data[tooltipItem.dataIndex]}%`;
        },
      },
    },
  },
});

export const chartLineDataStyled = ({
  labels,
  data,
  theme,
  tooltipLabel,
}: ChartLineDataStyledProps): any => ({
  labels,
  datasets: [
    {
      label: tooltipLabel,
      data,
      borderColor: theme.palette.primary.main,
      backgroundColor: theme.palette.primary.light,
      borderWidth: 1.5,
    },
  ],
});

export const chartLineDataStyledWithColorIndicator = ({
  colorIndicator,
  data,
  labels,
  tooltipLabel,
}: ChartLineDataStyledWithColorIndicatorProps): any => ({
  labels,
  datasets: [
    {
      label: tooltipLabel,
      data,
      borderColor: colorIndicator,
      backgroundColor: colorIndicator,
      borderWidth: 1.5,
    },
  ],
});

export const getFilterPeriodDates = (
  quantityOfMonthsInPeriod: DatePeriodFilters
): GetFilterPeriodDatesResponse => {
  const endDateFormattedToQuery = format(new Date(), 'yyyy-MM-dd');

  const sixMonthsAgoDate = sub(new Date(), { months: quantityOfMonthsInPeriod });

  const startDate = startOfMonth(sixMonthsAgoDate);
  const startDateFormattedToQuery = format(startDate, 'yyyy-MM-dd');

  return { startDateFormattedToQuery, endDateFormattedToQuery };
};

const getColorIndicator = (indicator: number, theme: Theme): string => {
  switch (indicator) {
    case 5:
      return theme.palette.auxGreen.main;
    case 4:
      return theme.palette.auxGreen.light;
    case 3:
      return theme.palette.warning.main;
    case 2:
      return theme.palette.warning.dark;
    case 1:
      return theme.palette.auxRed.main;
    default:
      return theme.palette.primary.main;
  }
};

export const getValueColorIndicator = ({
  value,
  maximumIndicator,
  theme,
}: GetValueColorIndicatorProps): string => {
  if (!value) return theme.palette.primary.main;

  const levelsOfIndicators = [5, 4, 3, 2, 1];
  const stepThatSeparateIndicators = maximumIndicator / levelsOfIndicators.length;

  let color = theme.palette.primary.main;

  levelsOfIndicators.forEach((indicator) => {
    if (value <= stepThatSeparateIndicators * indicator) {
      color = getColorIndicator(indicator, theme);
    }
  });

  return color;
};
